import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

// Interfaces
export interface User {
  _id: string;
  name: string;
  email: string;
  phone?: string;
}

export interface Popup {
  _id: string;
  name: string;
  imageUrl: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  targetUserIds?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPayload {
  userId?: string;
  title: string;
  body: string;
  type?: string;
  data?: Record<string, any>;
}

export interface SpecialOfferPayload {
  title: string;
  body: string;
  offerDetails: {
    offerId: string;
    validUntil: string;
    discountAmount?: string;
    couponCode?: string;
    [key: string]: any;
  };
}

const API_BASE_URL = API_BASE;

// API Functions
const fetchUsers = async (page = 1, limit = 20, searchTerm = '') => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/user/getUsers`, { // Backend route fixed to support this
      params: { page, limit, search: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

const fetchAllPopups = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/popup/popups`);
    return response.data;
  } catch (error) {
    console.error('Error fetching popups:', error);
    throw error;
  }
};

const createPopup = async (data: any) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/popup/popups`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating popup:', error);
    throw error;
  }
};

const updatePopup = async (id: string, data: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/popup/popups/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating popup:', error);
    throw error;
  }
};

const deletePopup = async (id: string) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/api/popup/popups/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting popup:', error);
    throw error;
  }
};

const togglePopupStatus = async (id: string) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/popup/popups/${id}/toggle-status`);
    return response.data;
  } catch (error) {
    console.error('Error toggling popup status:', error);
    throw error;
  }
};

const sendUserNotification = async (payload: NotificationPayload) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/send-user-notification`, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending user notification:', error);
    throw error;
  }
};

const sendBroadcast = async (payload: NotificationPayload) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/send-broadcast`, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending broadcast:', error);
    throw error;
  }
};

const sendSpecialOffer = async (payload: SpecialOfferPayload) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/send-special-offer`, payload);
    return response.data;
  } catch (error) {
    console.error('Error sending special offer:', error);
    throw error;
  }
};

const UnifiedManagementPanel: React.FC = () => {
  // Shared state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeModule, setActiveModule] = useState('notifications'); // 'notifications' or 'popups'
  const [searchTerm, setSearchTerm] = useState('');
  const [userPagination, setUserPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Notification state
  const [activeNotificationTab, setActiveNotificationTab] = useState('general');
  const [notification, setNotification] = useState({
    title: '',
    body: '',
    type: 'general',
    data: {}
  });
  const [specialOffer, setSpecialOffer] = useState({
    offerId: '',
    validUntil: '',
    discountAmount: '',
    couponCode: '',
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [notificationType, setNotificationType] = useState('single'); // 'single', 'multiple', 'all'
  const [showUserSelector, setShowUserSelector] = useState(false);

  // Popup state
  const [popups, setPopups] = useState<Popup[]>([]);
  const [isCreatingPopup, setIsCreatingPopup] = useState(false);
  const [isEditingPopup, setIsEditingPopup] = useState(false);
  const [currentPopup, setCurrentPopup] = useState<Popup | null>(null);
  const [popupFormData, setPopupFormData] = useState({
    name: '',
    isActive: false,
    startDate: '',
    endDate: '',
    targetUserIds: [] as string[],
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [popupTargetType, setPopupTargetType] = useState('all'); // 'all' or 'specific'
  const [showPopupUserSelector, setShowPopupUserSelector] = useState(false);

  useEffect(() => {
    if (activeModule === 'notifications') {
      setUsers([]); // Reset users before fetching new data
      fetchUserData();
    } else if (activeModule === 'popups') {
      fetchPopupData();
      fetchUserData();
    }
  }, [activeModule, userPagination.page, userPagination.limit, searchTerm]);

  // Debounce search term changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm) {
        fetchUserData();
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (activeModule === 'notifications') {
      setUserPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      });
      fetchUserData();
    }
  }, [activeModule]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const response = await fetchUsers(userPagination.page, userPagination.limit, searchTerm);

      if (response?.data) {
        setUsers(response.data); // Update users state
      } else {
        setUsers([]); // Ensure it's an empty array if no users are returned
      }

      // Update pagination details
      setUserPagination(prevState => ({
        ...prevState,
        total: response.pagination?.totalUsers || 0,
        totalPages: response.pagination?.totalPages || 1,
      }));

    } catch (error) {
      console.error('Error fetching users:', error);
      showError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const fetchPopupData = async () => {
    setLoading(true);
    try {
      const data = await fetchAllPopups();
      setPopups(data.data || []);
    } catch (error) {
      console.error('Error fetching popups:', error);
      showError('Failed to load popups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 3000);
  };

  // Notification handlers
  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNotification({ ...notification, [name]: value });
  };

  const handleSpecialOfferChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSpecialOffer({ ...specialOffer, [name]: value });
  };

  const handleUserSelection = (userId: string) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSendNotification = async () => {
    if (!notification.title || !notification.body) {
      showError('Title and body are required!');
      return;
    }

    try {
      if (activeNotificationTab === 'general') {
        if (notificationType === 'single' && selectedUsers.length === 1) {
          await sendUserNotification({
            userId: selectedUsers[0],
            title: notification.title,
            body: notification.body,
            type: notification.type,
          });
          showSuccess('Notification sent successfully to the selected user!');
        } else if (notificationType === 'multiple' && selectedUsers.length > 0) {
          // Send to multiple selected users one by one
          for (const userId of selectedUsers) {
            await sendUserNotification({
              userId,
              title: notification.title,
              body: notification.body,
              type: notification.type,
            });
          }
          showSuccess(`Notification sent successfully to ${selectedUsers.length} users!`);
        } else if (notificationType === 'all') {
          await sendBroadcast({
            title: notification.title,
            body: notification.body,
            type: 'broadcast',
          });
          showSuccess('Broadcast notification sent successfully to all users!');
        } else {
          showError('Please select at least one user!');
        }
      } else if (activeNotificationTab === 'special-offer') {
        if (!specialOffer.offerId || !specialOffer.validUntil) {
          showError('Offer ID and Valid Until date are required!');
          return;
        }

        await sendSpecialOffer({
          title: notification.title,
          body: notification.body,
          offerDetails: specialOffer,
        });

        showSuccess('Special offer notification sent successfully!');
      }

      // Reset form
      setNotification({
        title: '',
        body: '',
        type: 'general',
        data: {}
      });
      setSpecialOffer({
        offerId: '',
        validUntil: '',
        discountAmount: '',
        couponCode: '',
      });
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending notification:', error);
      showError('Failed to send notification. Please try again.');
    }
  };

  // Popup handlers
  const resetPopupForm = () => {
    setPopupFormData({
      name: '',
      isActive: false,
      startDate: '',
      endDate: '',
      targetUserIds: [],
    });
    setImageFile(null);
    setImagePreview(null);
    setCurrentPopup(null);
    setPopupTargetType('all');
  };

  const handlePopupInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setPopupFormData({
      ...popupFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handlePopupImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageFile(file);

      // Create image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePopup = () => {
    resetPopupForm();
    setIsCreatingPopup(true);
    setIsEditingPopup(false);
  };

  const handleEditPopup = (popup: Popup) => {
    setCurrentPopup(popup);
    setPopupFormData({
      name: popup.name,
      isActive: popup.isActive,
      startDate: new Date(popup.startDate).toISOString().split('T')[0],
      endDate: new Date(popup.endDate).toISOString().split('T')[0],
      targetUserIds: popup.targetUserIds || [],
    });
    setImagePreview(popup.imageUrl);
    setPopupTargetType(popup.targetUserIds && popup.targetUserIds.length > 0 ? 'specific' : 'all');
    setIsEditingPopup(true);
    setIsCreatingPopup(false);
  };

  const handleTogglePopupStatus = async (id: string) => {
    try {
      await axios.post(`${API_BASE_URL}/api/popup/popups/${id}/toggle-status`);
      fetchPopupData();
      showSuccess('Status updated successfully!');
    } catch (error) {
      console.error('Error toggling popup status:', error);
      showError('Failed to update status.');
    }
  };

  const handleDeletePopup = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this popup?')) {
      try {
        await deletePopup(id);
        fetchPopupData();
        showSuccess('Popup deleted successfully!');
      } catch (error) {
        console.error('Error deleting popup:', error);
        showError('Failed to delete popup. Please try again.');
      }
    }
  };

  const handlePopupUserSelection = (userId: string) => {
    const newSelectedUsers = [...popupFormData.targetUserIds];

    if (newSelectedUsers.includes(userId)) {
      const index = newSelectedUsers.indexOf(userId);
      newSelectedUsers.splice(index, 1);
    } else {
      newSelectedUsers.push(userId);
    }

    setPopupFormData({
      ...popupFormData,
      targetUserIds: newSelectedUsers,
    });
  };

  const handleSubmitPopup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!popupFormData.name || !popupFormData.startDate || !popupFormData.endDate) {
      showError('Please fill in all required fields.');
      return;
    }

    // Date validation
    if (new Date(popupFormData.endDate) < new Date(popupFormData.startDate)) {
      showError('End date cannot be earlier than the start date.');
      return;
    }

    if (!imageFile && !isEditingPopup) {
      showError('Please upload an image for the popup.');
      return;
    }

    setLoading(true);
    try {
      let finalImageUrl = currentPopup?.imageUrl || '';

      // 1. If there's a new image, upload it to S3 first
      if (imageFile) {
        // Get signed URL
        const { data: signedData } = await axios.post(`${API_BASE_URL}/api/popup/getSignedUrl`, {
          files: [{ fileName: imageFile.name, fileType: imageFile.type }]
        });

        const { uploadUrl, publicUrl } = signedData.data.signedUrls[0];

        // Upload directly to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: {
            'Content-Type': imageFile.type
          }
        });

        if (!uploadResponse.ok) {
          throw new Error('S3 Image upload failed');
        }

        finalImageUrl = publicUrl;
      }

      // 2. Prepare JSON payload
      const payload = {
        name: popupFormData.name,
        isActive: popupFormData.isActive,
        startDate: popupFormData.startDate,
        endDate: popupFormData.endDate,
        targetUserIds: popupTargetType === 'specific' ? popupFormData.targetUserIds : [],
        imageUrl: finalImageUrl
      };

      // 3. Save to database
      if (isEditingPopup && currentPopup) {
        await updatePopup(currentPopup._id, payload);
        showSuccess('Popup updated successfully!');
      } else {
        await createPopup(payload);
        showSuccess('Popup created successfully!');
      }

      fetchPopupData();
      resetPopupForm();
      setIsCreatingPopup(false);
      setIsEditingPopup(false);
    } catch (error) {
      console.error('Error saving popup:', error);
      showError('Failed to save popup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  };

  const handleSelectAllPopupUsers = () => {
    if (popupFormData.targetUserIds.length === users.length) {
      setPopupFormData({
        ...popupFormData,
        targetUserIds: [],
      });
    } else {
      setPopupFormData({
        ...popupFormData,
        targetUserIds: users.map(user => user._id),
      });
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  return (
    <div className="bg-white shadow-xl shadow-gray-200/50 rounded-2xl p-4 md:p-8 border border-gray-100 mb-10">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-3 md:p-4 bg-green-50 text-green-700 rounded-xl border border-green-100 text-sm font-medium animate-pulse">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 p-3 md:p-4 bg-red-50 text-red-700 rounded-xl border border-red-100 text-sm font-medium">
          {errorMessage}
        </div>
      )}

      {/* Main Tabs */}
      <div className="mb-6 md:mb-8 border-b border-gray-100 overflow-x-auto">
        <div className="flex -mb-px min-w-max">
          <button
            onClick={() => setActiveModule('notifications')}
            className={`mr-4 md:mr-8 py-3 px-4 md:px-6 text-sm font-bold uppercase tracking-wider transition-all ${activeModule === 'notifications'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-400 hover:text-gray-600'
              }`}
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveModule('popups')}
            className={`py-3 px-4 md:px-6 text-sm font-bold uppercase tracking-wider transition-all ${activeModule === 'popups'
              ? 'border-b-4 border-blue-600 text-blue-600'
              : 'text-gray-400 hover:text-gray-600'
              }`}
          >
            Popup Management
          </button>
        </div>
      </div>

      {/* Notification Module */}
      {activeModule === 'notifications' && (
        <div>
          {/* Notification Tabs */}
          <div className="mb-6 md:mb-8 border-b border-gray-100 overflow-x-auto">
            <div className="flex -mb-px min-w-max">
              <button
                onClick={() => setActiveNotificationTab('general')}
                className={`mr-4 md:mr-8 py-3 px-4 md:px-6 text-xs font-black uppercase tracking-widest transition-all ${activeNotificationTab === 'general'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                General Notification
              </button>
              <button
                onClick={() => setActiveNotificationTab('special-offer')}
                className={`py-3 px-4 md:px-6 text-xs font-black uppercase tracking-widest transition-all ${activeNotificationTab === 'special-offer'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                Special Offer
              </button>
            </div>
          </div>

          {/* Common Notification Fields */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Notification Title</label>
            <input
              type="text"
              name="title"
              value={notification.title}
              onChange={handleNotificationChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter notification title"
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">Notification Body</label>
            <textarea
              name="body"
              value={notification.body}
              onChange={handleNotificationChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
              placeholder="Enter notification message"
            />
          </div>

          {/* Notification Type - General */}
          {activeNotificationTab === 'general' && (
            <>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Notification Type</label>
                <input
                  type="text"
                  name="type"
                  value={notification.type}
                  onChange={handleNotificationChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="E.g., general, promotion, update"
                />
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <label className="block text-gray-700 text-sm font-bold mr-4">Send To:</label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="single"
                        checked={notificationType === 'single'}
                        onChange={() => {
                          setNotificationType('single');
                          setSelectedUsers([]);
                          setShowUserSelector(true);
                        }}
                        className="form-radio h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Single User</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="multiple"
                        checked={notificationType === 'multiple'}
                        onChange={() => {
                          setNotificationType('multiple');
                          setSelectedUsers([]);
                          setShowUserSelector(true);
                        }}
                        className="form-radio h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Multiple Users</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="all"
                        checked={notificationType === 'all'}
                        onChange={() => {
                          setNotificationType('all');
                          setSelectedUsers([]);
                          setShowUserSelector(false);
                        }}
                        className="form-radio h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">All Users</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* User Selector */}
              {showUserSelector && (
                <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Select Users</h3>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="Search users..."
                      />
                      {notificationType === 'multiple' && (
                        <button
                          type="button"
                          onClick={handleSelectAllUsers}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded"
                        >
                          {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>
                  </div>

                  {loading ? (
                    <div className="flex justify-center py-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : users.length === 0 ? (
                    <p className="text-center py-10 text-gray-400 font-medium italic">No users found.</p>
                  ) : (
                    <div className="overflow-hidden">
                      {/* Desktop Table View */}
                      <div className="hidden md:block max-h-80 overflow-y-auto border border-gray-100 rounded-xl">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">
                                {notificationType === 'multiple' ? 'Select' : 'Select One'}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Name</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Email</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Phone</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {users.map((user) => (
                              <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <input
                                    type={notificationType === 'single' ? 'radio' : 'checkbox'}
                                    name="selectedUser"
                                    checked={selectedUsers.includes(user._id)}
                                    onChange={() => {
                                      if (notificationType === 'single') {
                                        setSelectedUsers([user._id]);
                                      } else {
                                        handleUserSelection(user._id);
                                      }
                                    }}
                                    className={notificationType === 'single' ? 'form-radio h-5 w-5 text-blue-600 border-gray-300' : 'form-checkbox h-5 w-5 text-blue-600 border-gray-300 rounded'}
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-bold text-gray-900">{user.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 font-medium">{user.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                  {user.phone || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden space-y-3 max-h-80 overflow-y-auto pr-1">
                        {users.map((user) => (
                          <div
                            key={user._id}
                            onClick={() => {
                              if (notificationType === 'single') {
                                setSelectedUsers([user._id]);
                              } else {
                                handleUserSelection(user._id);
                              }
                            }}
                            className={`p-3 rounded-xl border transition-all ${selectedUsers.includes(user._id)
                              ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100'
                              : 'bg-white border-gray-100'
                              }`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="min-w-0 flex-1">
                                <h4 className="text-xs font-black text-gray-900 truncate uppercase tracking-tight">{user.name}</h4>
                                <p className="text-[10px] text-gray-400 font-bold truncate mt-1">{user.email}</p>
                                {user.phone && <p className="text-[9px] text-blue-500 font-black mt-1">{user.phone}</p>}
                              </div>
                              <div className="shrink-0 pt-1">
                                <input
                                  type={notificationType === 'single' ? 'radio' : 'checkbox'}
                                  checked={selectedUsers.includes(user._id)}
                                  readOnly
                                  className={notificationType === 'single' ? 'form-radio h-5 w-5 text-blue-600' : 'form-checkbox h-5 w-5 text-blue-600 rounded-md'}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between mt-4">
                    <div>
                      <span className="text-sm text-blue-600 font-medium">
                        {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setUserPagination({ ...userPagination, page: Math.max(1, userPagination.page - 1) })}
                        disabled={userPagination.page === 1}
                        className={`px-3 py-1 rounded ${userPagination.page === 1 ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {userPagination.page} of {userPagination.totalPages}
                      </span>
                      <button
                        onClick={() => setUserPagination({ ...userPagination, page: Math.min(userPagination.totalPages, userPagination.page + 1) })}
                        disabled={userPagination.page === userPagination.totalPages}
                        className={`px-3 py-1 rounded ${userPagination.page === userPagination.totalPages ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notification Type - Special Offer */}
          {activeNotificationTab === 'special-offer' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Offer ID</label>
                <input
                  type="text"
                  name="offerId"
                  value={specialOffer.offerId}
                  onChange={handleSpecialOfferChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Unique offer identifier"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Valid Until</label>
                <input
                  type="date"
                  name="validUntil"
                  value={specialOffer.validUntil}
                  onChange={handleSpecialOfferChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Discount Amount</label>
                <input
                  type="text"
                  name="discountAmount"
                  value={specialOffer.discountAmount}
                  onChange={handleSpecialOfferChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="E.g., 20% or $50"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Coupon Code</label>
                <input
                  type="text"
                  name="couponCode"
                  value={specialOffer.couponCode}
                  onChange={handleSpecialOfferChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="E.g., SPECIAL50"
                />
              </div>
            </div>
          )}

          {/* Send Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSendNotification}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </div>
      )}

      {/* Popup Management Module */}
      {activeModule === 'popups' && (
        <div>
          {/* Action Buttons */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-bold">Popup Management</h2>
            <button
              onClick={handleCreatePopup}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              Create New Popup
            </button>
          </div>

          {/* Popup Form */}
          {(isCreatingPopup || isEditingPopup) && (
            <div className="mb-8 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">
                {isCreatingPopup ? 'Create New Popup' : 'Edit Popup'}
              </h3>

              <form onSubmit={handleSubmitPopup}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Popup Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={popupFormData.name}
                      onChange={handlePopupInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      placeholder="Enter popup name"
                      required
                    />
                  </div>

                  <div className="flex items-center h-full pt-6">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={popupFormData.isActive}
                        onChange={handlePopupInputChange}
                        className="form-checkbox h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Active</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={popupFormData.startDate}
                      onChange={handlePopupInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={popupFormData.endDate}
                      onChange={handlePopupInputChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Popup Image <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePopupImageChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-1">Preview:</p>
                      <img
                        src={imagePreview}
                        alt="Popup preview"
                        className="h-32 object-contain border rounded"
                      />
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Target Users</label>
                  <div className="flex items-center space-x-4 mb-2">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="all"
                        checked={popupTargetType === 'all'}
                        onChange={() => {
                          setPopupTargetType('all');
                          setShowPopupUserSelector(false);
                          setPopupFormData({
                            ...popupFormData,
                            targetUserIds: [],
                          });
                        }}
                        className="form-radio h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">All Users</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="specific"
                        checked={popupTargetType === 'specific'}
                        onChange={() => {
                          setPopupTargetType('specific');
                          setShowPopupUserSelector(true);
                        }}
                        className="form-radio h-5 w-5 text-blue-600"
                      />
                      <span className="ml-2 text-gray-700">Specific Users</span>
                    </label>
                  </div>

                  {/* User Selector for Popups */}
                  {showPopupUserSelector && (
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium">Select Target Users</h4>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="shadow appearance-none border rounded py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            placeholder="Search users..."
                          />
                          <button
                            type="button"
                            onClick={handleSelectAllPopupUsers}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-1 px-3 rounded text-sm"
                          >
                            {popupFormData.targetUserIds.length === users.length ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                      </div>

                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Select
                              </th>
                              <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                              </th>
                              <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                              <tr key={user._id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={popupFormData.targetUserIds.includes(user._id)}
                                    onChange={() => handlePopupUserSelection(user._id)}
                                    className="form-checkbox h-4 w-4 text-blue-600"
                                  />
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-2 text-sm text-blue-600 font-medium">
                        {popupFormData.targetUserIds.length} user{popupFormData.targetUserIds.length !== 1 ? 's' : ''} selected
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      resetPopupForm();
                      setIsCreatingPopup(false);
                      setIsEditingPopup(false);
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (isEditingPopup ? 'Update Popup' : 'Create Popup')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Popup List */}
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-4 text-gray-800">Active Popups</h3>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : popups.length === 0 ? (
              <p className="text-center py-10 text-gray-400 font-medium italic">No popups found.</p>
            ) : (
              <div className="rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500">
                {/* Desktop Table View */}
                <div className="hidden md:block shadow-xl shadow-gray-200/40 border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Popup</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Status</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Date Range</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {popups.map((popup) => (
                        <tr key={popup._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-12 w-12">
                                <img
                                  className="h-12 w-12 rounded-xl object-cover border-2 border-gray-100"
                                  src={popup.imageUrl}
                                  alt={popup.name}
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-bold text-gray-900 uppercase tracking-tight">{popup.name}</div>
                                <div className="text-[10px] text-gray-400 font-bold uppercase">ID: {popup._id.slice(-6)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${popup.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                              {popup.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-xs font-bold text-gray-600">
                              {new Date(popup.startDate).toLocaleDateString('en-GB')} - {new Date(popup.endDate).toLocaleDateString('en-GB')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center space-x-3">
                            <button onClick={() => handleTogglePopupStatus(popup._id)} className="text-blue-600 hover:text-blue-900 text-xs font-black uppercase tracking-widest">Toggle</button>
                            <button onClick={() => handleEditPopup(popup)} className="text-indigo-600 hover:text-indigo-900 text-xs font-black uppercase tracking-widest">Edit</button>
                            <button onClick={() => handleDeletePopup(popup._id)} className="text-red-600 hover:text-red-900 text-xs font-black uppercase tracking-widest">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {popups.map((popup) => (
                    <div key={popup._id} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-lg shadow-gray-200/50">
                      <div className="flex gap-4 mb-4">
                        <img
                          className="h-16 w-16 rounded-xl object-cover border-2 border-gray-50"
                          src={popup.imageUrl}
                          alt={popup.name}
                        />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-black text-gray-900 uppercase truncate leading-tight">{popup.name}</h4>
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${popup.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {popup.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-xl mb-4 text-[10px]">
                        <div>
                          <span className="block text-gray-400 font-bold uppercase mb-0.5">Start Date</span>
                          <span className="text-gray-700 font-black">{new Date(popup.startDate).toLocaleDateString('en-GB')}</span>
                        </div>
                        <div>
                          <span className="block text-gray-400 font-bold uppercase mb-0.5">End Date</span>
                          <span className="text-gray-700 font-black">{new Date(popup.endDate).toLocaleDateString('en-GB')}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button onClick={() => handleTogglePopupStatus(popup._id)} className="flex-1 py-2 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-blue-100 transition-all active:scale-95">Status</button>
                        <button onClick={() => handleEditPopup(popup)} className="flex-1 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-indigo-100 transition-all active:scale-95">Edit</button>
                        <button onClick={() => handleDeletePopup(popup._id)} className="flex-1 py-2 bg-red-50 text-red-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-red-100 transition-all active:scale-95">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedManagementPanel;